import React, { useState, useMemo, useCallback, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
    renderCellContent,
    getAccordionMemberRowRender,
    useMemberGroupToggle,
    getAnalystFilteredSortedData,
    getFlattenedData,
    saveDetailStateToStorage,
    EnergyStafferNoDealOnFilterState,
    EnergyBankerNoDealOnFilterState,
    EnergyAnalystsEmptyState,
} from "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import {
    renderlastUpdatedTooltipHeader,
    formatLastUpdatedDate,
    renderTooltipHeader,
    createColumnMenu,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { getLastUpdatedDateColorWeeks, NameAndNoDealsBadge } from "../../../dashboard-util/dashboard-utils";
import { FluentAvatar } from "../../../../../components/FluentUI/Fluent-Avatar";
import  { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { LoginContext } from "../../../../../services/api/authentication-api";
import EnergyAnalystDetailGridModal from "../energy-analyst-time-log/energy-analyst-time-log-modal.jsx";
import {
    GetEnergyBankerTimeLogForStaffer,
    GetEnergySIBankerTimeLogForStaffer
   } from "../../../../../services/api/dashboard-api";
import "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log.scss";

const EnergyAnalystsDetailGrid = ({
    data = [],
    setIsEnergyStafferTimeLogLoading,
    fetchEnergyTechStafferTimeLog,
    setIsEnergyStafferDealResourceLogLoading,
    fetchStafferDealDetailsandResourcingLog,
    showToast,
    onItemChange,
    detailState,
    onDetailStateChange,
    onVisibleDataChange,
    resetDataState,
    isSI = false,
    fetchEnergyJuniorStaffingIndex,
    setIsEnergyJuniorStaffingIndexLoading
 }) => {
    const user = useContext(LoginContext);
    const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
    const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
    const { groupState, toggleGroup, setGroupState } = useMemberGroupToggle();
    const [showAnalyst, setShowAnalyst] = useState(false);
    const [analystLoading, setAnalystLoading] = useState(false);
    const [selectedAnalyst, setSelectedAnalyst] = useState(null);
    const [selectedAnalystData, setSelectedAnalystData] = useState(null);

    const flattenedData = useMemo(() => getFlattenedData(data), [data]);

    const filteredData = useMemo(() =>
        getAnalystFilteredSortedData(flattenedData, detailState.filter, detailState.sort),
    [flattenedData, detailState.filter, detailState.sort]);

    const visibleData = useMemo(() => filteredData, [filteredData]);
    const visibleLength = useMemo(() => visibleData?.length ?? 0, [visibleData]);

    useEffect(() => {
        if (onVisibleDataChange) {
            const visibleLenghtValue =  visibleData?.length ?? 0;
            onVisibleDataChange({
                isEmpty: visibleLenghtValue === 0,
            });
        }
    }, [visibleData, onVisibleDataChange]);

    const columnMenu = createColumnMenu(flattenedData);
    const AccordionRowRender =
         useMemo(() => getAccordionMemberRowRender(filteredData, groupState, toggleGroup, 8, "brand"), [filteredData, groupState]);
    const isEmpty = visibleData?.length === 0;
    const finalGridData = isEmpty
        ? [{
            isEmptyRow: true,
            showSearchEmptyState: false,
            isFilterEmptyState: (detailState.filter?.filters?.length ?? 0) > 0,
          }]
        : visibleData;

    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, detailState.filter),
    [detailState.filter]);

    const onHide = () => {
        setShowAnalyst(false);
        setSelectedAnalyst(null);
    };

    const onRowClick = async (event) => {
        setSelectedAnalyst(event.dataItem);
        const selectedUserId = event.dataItem?.UserId;
        if (!selectedUserId)
            return;
        await getEnergyBankerTimeLog(selectedUserId);
    };

    const getEnergyBankerTimeLog = useCallback(async (userId) => {
        try {
            setShowAnalyst(true);
            setAnalystLoading(true);
            const endpoint = isSI ? GetEnergySIBankerTimeLogForStaffer : GetEnergyBankerTimeLogForStaffer;
            const response = await endpoint(userId);
            if (response?.data) {
                const fetchedData = response.data;
                setSelectedAnalystData(fetchedData);
            } else {
                console.error("No data returned from GetEnergyBankerTimeLogForStaffer");
                showToast("No data returned from GetEnergyBankerTimeLogForStaffer", "error");
            }
        } catch (error) {
            console.error("Error fetching energy banker time log for staffer:", error);
            showToast("Error fetching energy banker time log for staffer", "error");
        } finally {
            setAnalystLoading(false);
        }
      }, [isSI]);

    return (
        <React.Fragment>
            <div className="detail-grid-scroll-container energy-staffer-widgets">
                <Grid
                    data={finalGridData}
                    sortable={true}
                    sort={detailState.sort}
                    filter={detailState.filter}
                    onSortChange={(e) => {
                        onDetailStateChange({ ...detailState, sort: e.sort });
                        saveDetailStateToStorage(user.userID, { ...detailState, sort: e.sort }, "", "analystsTimeLog");
                        setGroupState({ ...groupState });
                    }}
                    onFilterChange={(e) => {
                        onDetailStateChange({ ...detailState, filter: e.filter });
                        saveDetailStateToStorage(user.userID, { ...detailState, filter: e.filter }, "", "analystsTimeLog");
                    }}
                    editable="false"
                    onRowClick={onRowClick}
                    rowRender={AccordionRowRender}
                    navigable={true}
                    keyboardNavigation={true}
                    dataItemKey="Name"
                    resizable={true}
                >
                    <Column
                        field="Name"
                        title={renderTooltipHeader(isSI ? "Intern" : "Banker")}
                        editable={false}
                        filter="text"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("Name") ? "filtered" : "" }
                        cell={(props) =>
                            props.dataItem.isEmptyRow
                            ?   <td colSpan={8} className="text-center k-table-td">
                                    { props.dataItem.isFilterEmptyState
                                        ?   <EnergyBankerNoDealOnFilterState resetDataState={resetDataState} />
                                        :   <EnergyAnalystsEmptyState />
                                    }
                                </td>
                            :   <td className="background-white k-table-td d-flex">
                                    <div className="dashboard-username" style={{ pointerEvents: 'none' }}>
                                        <FluentAvatar
                                            size={28}
                                            name={props.dataItem.Name}
                                            className="task-member-user mr-2"
                                        />
                                        <NameAndNoDealsBadge onRowClick={() => onRowClick(props)} name={props.dataItem.Name ?? '—'} activeDeals={props.dataItem.ActiveDeals } />
                                    </div>
                                </td>
                          }
                    />
                    <Column
                        field="TotalHoursLastWeek"
                        title={renderTooltipHeader("Last Wk Hrs (M-F)")}
                        editable={false}
                        filter="numeric"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("TotalHoursLastWeek") ? "filtered" : "" }
                        cell={(props) =>
                            renderCellContent(
                                visibleData.length,
                                null,
                                <td className="background-white k-table-td">{props.dataItem?.TotalHoursLastWeek ?? 0}</td>
                            )
                        }
                    />
                    <Column
                        field="TotalWeekendHoursLastWeek"
                        title={renderTooltipHeader("Last Wknd Hrs (S-S)")}
                        editable={false}
                        filter="numeric"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("TotalWeekendHoursLastWeek") ? "filtered" : "" }
                        cell={(props) =>
                            renderCellContent(
                                visibleData.length,
                                null,
                                <td className="background-white k-table-td">{props.dataItem?.TotalWeekendHoursLastWeek ?? 0}</td>
                            )
                        }
                    />
                    <Column
                        field="TotalActualHoursLastWeek"
                        title={renderTooltipHeader("Actual Total Hrs (M-S)")}
                        editable={false}
                        filter="numeric"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("TotalActualHoursLastWeek") ? "filtered" : "" }
                        cell={(props) =>
                                renderCellContent(
                                    visibleData.length,
                                    null,
                                    <td className="background-white k-table-td">{props.dataItem?.TotalActualHoursLastWeek ?? 0}</td>
                                )
                            }
                    />
                    <Column
                        field="TotalPriorWeekEstimate"
                        title={renderTooltipHeader("Prior Wk Est.")}
                        editable={false}
                        filter="numeric"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("TotalPriorWeekEstimate") ? "filtered" : "" }
                        cell={(props) =>
                            renderCellContent(
                                visibleData.length,
                                null,
                                <td className="background-white k-table-td">{props.dataItem?.TotalPriorWeekEstimate ?? 0 }</td>
                            )
                        }
                    />
                    <Column
                        field="TotalHoursCurrentWeek"
                        title={renderTooltipHeader("Current Wk Hrs")}
                        editable={false}
                        filter="numeric"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("TotalHoursCurrentWeek") ? "filtered" : "" }
                        cell={(props) =>
                            renderCellContent(
                                visibleData.length,
                                null,
                                <td className="background-white k-table-td">{props.dataItem?.TotalHoursCurrentWeek ?? 0}</td>
                            )
                        }
                    />
                    <Column
                        field="PercentageHoursOnLiveDeals"
                        title={renderTooltipHeader("% of Hrs on Live Deals")}
                        editable={false}
                        filter="numeric"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("PercentageHoursOnLiveDeals") ? "filtered" : "" }
                        cell={(props) =>
                            renderCellContent(
                                visibleData.length,
                                null,
                                <td className="background-white k-table-td">
                                    {`${props.dataItem?.PercentageHoursOnLiveDeals}%` ?? 0}
                                </td>
                            )
                        }
                    />
                    <Column
                        field="LastUpdatedString"
                        title={renderlastUpdatedTooltipHeader("Last Updated")}
                        editable={false}
                        filter="date"
                        columnMenu={columnMenu}
                        headerClassName={ columnIsFiltered("LastUpdatedString") ? "filtered" : "" }
                        cell={(props) => (
                            renderCellContent(visibleData.length, null, (
                                <td className="background-white k-table-td"
                                    style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdatedString) }}
                                >
                                    <span>{formatLastUpdatedDate(props.dataItem.LastUpdatedString) ?? "—"}</span>
                                </td>
                            ))
                        )}
                    />
                </Grid>
            </div>
            {   showAnalyst && (
                <EnergyAnalystDetailGridModal
                    show={showAnalyst}
                    loading={analystLoading}
                    onHide={onHide}
                    selectedAnalyst={selectedAnalyst}
                    selectedAnalystData={selectedAnalystData}
                    visibleLength={visibleLength}
                    fetchEnergyTechStafferTimeLog={fetchEnergyTechStafferTimeLog}
                    setIsEnergyStafferTimeLogLoading={setIsEnergyStafferTimeLogLoading}
                    setIsEnergyStafferDealResourceLogLoading={setIsEnergyStafferDealResourceLogLoading}
                    fetchStafferDealDetailsandResourcingLog={fetchStafferDealDetailsandResourcingLog}
                    showToast={showToast}
                    isSI={isSI}
                    fetchEnergyJuniorStaffingIndex={fetchEnergyJuniorStaffingIndex}
                    setIsEnergyJuniorStaffingIndexLoading={setIsEnergyJuniorStaffingIndexLoading}
                />
            )}
        </React.Fragment>
    );
};

EnergyAnalystsDetailGrid.propTypes = {
    data: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          UserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          Name: PropTypes.string,
          IsActive: PropTypes.bool,
          ActiveDeals: PropTypes.bool,
          LastUpdatedString: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
          ]),
          TotalHoursLastWeek: PropTypes.number,
          TotalWeekendHoursLastWeek: PropTypes.number,
          TotalActualHoursLastWeek: PropTypes.number,
          TotalPriorWeekEstimate: PropTypes.number,
          TotalHoursCurrentWeek: PropTypes.number,
          PercentageHoursOnLiveDeals: PropTypes.number,
        }),
        PropTypes.shape({
          isEmptyRow: PropTypes.bool,
          showSearchEmptyState: PropTypes.bool,
          isFilterEmptyState: PropTypes.bool,
        }),
      ])
    ).isRequired,
    setIsEnergyStafferTimeLogLoading: PropTypes.func,
    fetchEnergyTechStafferTimeLog: PropTypes.func,
    setIsEnergyStafferDealResourceLogLoading: PropTypes.func,
    fetchStafferDealDetailsandResourcingLog: PropTypes.func,
    showToast: PropTypes.func,
    onItemChange: PropTypes.func,
    detailState: PropTypes.shape({
      sort: PropTypes.arrayOf(
        PropTypes.shape({
          field: PropTypes.string,
          dir: PropTypes.oneOf(["asc", "desc"]),
        })
      ),
      filter: PropTypes.shape({
        logic: PropTypes.oneOf(["and", "or"]),
        filters: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.shape({
              field: PropTypes.string,
              operator: PropTypes.string,
              value: PropTypes.any,
            }),
            PropTypes.shape({
              logic: PropTypes.oneOf(["and", "or"]),
              filters: PropTypes.array,
            }),
          ])
        ),
      }),
    }),
    onDetailStateChange: PropTypes.func,
    onVisibleDataChange: PropTypes.func,
    resetDataState: PropTypes.func,
    isSI: PropTypes.bool,
    fetchEnergyJuniorStaffingIndex: PropTypes.func,
    setIsEnergyJuniorStaffingIndexLoading: PropTypes.func,
  };

export default EnergyAnalystsDetailGrid;
